import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { ensureClarityLoaded } from '../services/clarity';
import {
  COOKIE_PREFERENCES_OPEN_EVENT,
  getCookieConsent,
  hasSavedCookieConsent,
  saveCookieConsent,
} from '../services/cookieConsent';

export default function CookieConsent() {
  const { t } = useI18n();
  const [consent, setConsent] = React.useState(() => getCookieConsent());
  const [draftAnalytics, setDraftAnalytics] = React.useState(() => getCookieConsent().analytics);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (consent.analytics) {
      ensureClarityLoaded();
    }
  }, [consent.analytics]);

  React.useEffect(() => {
    function handleOpenPreferences() {
      setDraftAnalytics(consent.analytics);
      setIsModalOpen(true);
    }

    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
    return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
  }, [consent.analytics]);

  React.useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }
    }

    if (!isModalOpen) return undefined;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  function persistConsent(nextValue) {
    const previousAnalytics = consent.analytics;
    const nextConsent = saveCookieConsent(nextValue);

    setConsent(nextConsent);
    setDraftAnalytics(nextConsent.analytics);
    setIsModalOpen(false);

    if (previousAnalytics && !nextConsent.analytics) {
      window.location.reload();
    }
  }

  const shouldShowBanner = !hasSavedCookieConsent(consent);

  return (
    <>
      {shouldShowBanner ? (
        <section className="yk-cookieBanner" aria-label={t('cookiesConsent.bannerTitle')}>
          <div className="yk-cookieBannerInner yk-card">
            <div className="yk-cookieBannerContent">
              <p className="yk-cookieEyebrow">{t('cookiesConsent.eyebrow')}</p>
              <h2 className="yk-h2">{t('cookiesConsent.bannerTitle')}</h2>
              <p className="yk-text">{t('cookiesConsent.bannerBody')}</p>
              <p className="yk-cookieLinksText">
                <Link to="/politica-confidentialitate">{t('legal.privacyPolicy')}</Link>
                <span> · </span>
                <Link to="/politica-cookie-uri">{t('legal.cookiePolicy')}</Link>
              </p>
            </div>

            <div className="yk-cookieActions">
              <button
                type="button"
                className="yk-btn yk-btnGhost"
                onClick={() => persistConsent({ analytics: false, status: 'rejected' })}
              >
                {t('cookiesConsent.rejectOptional')}
              </button>
              <button
                type="button"
                className="yk-btn yk-btnGhost"
                onClick={() => {
                  setDraftAnalytics(consent.analytics);
                  setIsModalOpen(true);
                }}
              >
                {t('cookiesConsent.customize')}
              </button>
              <button
                type="button"
                className="yk-btn yk-btnPrimary"
                onClick={() => persistConsent({ analytics: true, status: 'accepted' })}
              >
                {t('cookiesConsent.acceptAnalytics')}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {isModalOpen ? (
        <div
          className="yk-cookieModalBackdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          <section
            className="yk-cookieModal yk-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="yk-cookieModalTitle"
          >
            <div className="yk-cookieModalHeader">
              <div>
                <p className="yk-cookieEyebrow">{t('cookiesConsent.settingsEyebrow')}</p>
                <h2 className="yk-h2" id="yk-cookieModalTitle">
                  {t('cookiesConsent.modalTitle')}
                </h2>
              </div>
              <button
                type="button"
                className="yk-cookieModalClose"
                aria-label={t('cookiesConsent.close')}
                onClick={() => setIsModalOpen(false)}
              >
                ×
              </button>
            </div>

            <p className="yk-text">{t('cookiesConsent.modalBody')}</p>

            <div className="yk-cookiePreferenceList">
              <article className="yk-cookiePreferenceItem">
                <div>
                  <h3 className="yk-h3">{t('cookiesConsent.necessaryTitle')}</h3>
                  <p className="yk-text">{t('cookiesConsent.necessaryDescription')}</p>
                </div>
                <span className="yk-cookieStatusPill">{t('cookiesConsent.alwaysActive')}</span>
              </article>

              <label className="yk-cookiePreferenceItem yk-cookiePreferenceItemToggle">
                <div>
                  <h3 className="yk-h3">{t('cookiesConsent.analyticsTitle')}</h3>
                  <p className="yk-text">{t('cookiesConsent.analyticsDescription')}</p>
                </div>
                <span className="yk-cookieSwitchWrap">
                  <input
                    className="yk-cookieSwitchInput"
                    type="checkbox"
                    checked={draftAnalytics}
                    onChange={(event) => setDraftAnalytics(event.target.checked)}
                  />
                  <span className="yk-cookieSwitch" aria-hidden="true" />
                </span>
              </label>
            </div>

            <p className="yk-cookieFinePrint">{t('cookiesConsent.withdrawNote')}</p>

            <div className="yk-cookieActions is-modal">
              <button type="button" className="yk-btn yk-btnGhost" onClick={() => setIsModalOpen(false)}>
                {t('cookiesConsent.cancel')}
              </button>
              <button
                type="button"
                className="yk-btn yk-btnPrimary"
                onClick={() =>
                  persistConsent({
                    analytics: draftAnalytics,
                    status: draftAnalytics ? 'customized' : 'rejected',
                  })
                }
              >
                {t('cookiesConsent.savePreferences')}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}